import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button, Field, Input, Spinner } from '@mkcore/ui';
import { meQuery } from '@/lib/session';
import { EmailCode, PortalCard, startEntra } from '@/features/portal/login';

const search = z.object({ redirect: z.string().optional(), error: z.coerce.string().optional() }).loose();

/** The login of the client portal (CO-0661, CO-0033, CO-0034, ADR-0028), with the privacy note (CO-0665). */
export const Route = createFileRoute('/portal/login')({
  validateSearch: search,
  component: PortalLogin,
});

function PortalLogin() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { redirect, error } = Route.useSearch();
  const target = redirect && redirect.startsWith('/portal') ? redirect : '/portal';
  const options = useQuery({
    queryKey: ['login-options'],
    queryFn: async () => (await fetch('/api/login-options')).json() as Promise<{ entra: boolean; devLogin: boolean }>,
  });
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(!!error);
  const [codeOpen, setCodeOpen] = useState(false);
  const [email, setEmail] = useState('');
  const done = async () => {
    qc.removeQueries({ queryKey: meQuery.queryKey });
    await navigate({ to: target });
  };
  const microsoft = async () => {
    setBusy(true);
    if (!(await startEntra(target))) {
      setBusy(false);
      setFailed(true);
    }
  };
  const devLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const res = await fetch('/api/auth/dev-login', { method: 'POST', headers: { 'content-type': 'application/json' }, credentials: 'same-origin', body: JSON.stringify({ email }) });
    if (!res.ok) return setFailed(true);
    await done();
  };
  return (
    <PortalCard title={t('portal.login.title')} subtitle={t('portal.login.subtitle')}>
      {options.isLoading && <Spinner label={t('common.loading')} />}
      {failed && (
        <p role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger">
          {t('portal.login.failed')}
        </p>
      )}
      {options.data?.entra && (
        <Button size="lg" loading={busy} onClick={() => void microsoft()}>
          {t('portal.login.microsoft')}
        </Button>
      )}
      {codeOpen ? (
        <EmailCode onSignedIn={done} />
      ) : (
        <Button variant="secondary" onClick={() => setCodeOpen(true)}>
          {t('portal.login.withCode')}
        </Button>
      )}
      {options.data?.devLogin && (
        <form onSubmit={(e) => void devLogin(e)} className="grid gap-3 rounded-md border border-dashed border-line p-4">
          <p className="text-sm font-bold">{t('auth.devTitle')}</p>
          <Field label={t('common.email')}>{(p) => <Input {...p} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />}</Field>
          <Button type="submit" variant="secondary">
            {t('auth.devButton')}
          </Button>
        </form>
      )}
    </PortalCard>
  );
}
